import { ERROR_FAILED_TO_SELECT_DESTINATION, ERROR_FAILED_TO_SELECT_SOURCE } from "../constants/errors";
import { paramDestDs, paramDestPath, paramLocation, paramSourceDs, paramSourcePath } from "../constants/parameterNames";
import {  typeOption } from '../constants/parameterTypes';
import { getParamValue } from "../helpers/parameter.accessors";
import { getDataSource } from "../helpers/qRules.dataSource.helpers";
import { getValidNode, getValidNodeSet } from "../helpers/qRules.node.helpers";

const locationFirstChild = "firstChild";
const locationLastChild = "lastChild";
const locationBefore = "before";
const locationAfter = "after";
const locationDescription = String.format(
    "The location relative to the destination node where the node should be inserted ({0}, {1}, {2} or {3}). The default value is {1}.",
    locationFirstChild,
    locationLastChild,
    locationBefore,
    locationAfter
);
const errorInsertFail = "An error occurred trying to add the node to the specified location.";
const errorInvalidLocation = String.format(
    "Invalid location value. Allowed values are {0}, {1}, {2} and {3}.",
    locationFirstChild,
    locationLastChild,
    locationBefore,
    locationAfter
);
const errorRemoveOriginalFail = "An error occurred trying to delete the node being moved.";

export const LOCATION_PARAMETER = {
    name: paramLocation,
    description: locationDescription,
    type: typeOption,
    options: [
        {
            name: "First Child",
            value: locationFirstChild,
        },
        {
            name: "Last Child",
            value: locationLastChild,
        },
        {
            name: "Before",
            value: locationBefore,
        },
        {
            name: "After",
            value: locationAfter,
        },
    ],
};

function getInsertedNodeSelectionPath(location) {
    switch (location) {
        case locationFirstChild:
            return "node()[1]";
        case locationLastChild:
            return "node()[last()]";
        case locationBefore:
            return "preceding-sibling::node()[1]";
        case locationAfter:
            return "following-sibling::node()[1]";
        default:
            throw new Error(errorInvalidLocation);
    }
}

function getInsertAction(location, destNode) {
    switch (location) {
        case locationFirstChild:
            const firstNode = destNode.selectSingle("node()[1]");

            if (firstNode) {
                return firstNode.insertBeforeAsync;
            }

            return destNode.appendChildAsync;

        case locationLastChild:
            return destNode.appendChildAsync;

        case locationBefore:
            return destNode.insertBeforeAsync;

        case locationAfter:
            return destNode.insertAfterAsync;

        default:
            throw new Error(errorInvalidLocation);
    }
}

function performInsert(location, destNode, sourceNode) {
    const insertNode = sourceNode.getNode().cloneNode(true);

    return getInsertAction(location, destNode)(insertNode);
}

function performInsertAction(location, destNode, sourceNode) {
    return performInsert(location, destNode, sourceNode)
        .catch(function () {
            throw new Error(errorInsertFail);
        })
        .then(function () {
            return destNode.selectSingle(getInsertedNodeSelectionPath(location));
        });
}

const executeAsync = (isMove) => async ({ parameters, dataSources }) => {
    const sourceDsName = getParamValue(parameters, paramSourceDs);
    const sourcePath = getParamValue(parameters, paramSourcePath);

    const destDsName = getParamValue(parameters, paramDestDs);
    const destPath = getParamValue(parameters, paramDestPath);

    let location = getParamValue(parameters, paramLocation, locationLastChild);

    const sourceDs = getDataSource(dataSources, sourceDsName);
    const sourceNodes = getValidNodeSet(sourceDs, sourcePath, ERROR_FAILED_TO_SELECT_SOURCE);

    const destDs = getDataSource(dataSources, destDsName);
    let destNode = getValidNode(destDs, destPath, ERROR_FAILED_TO_SELECT_DESTINATION);

    for (let sourceNode of sourceNodes) {
        const resultNode = await performInsertAction(location, destNode, sourceNode);

        // after the first insertion, all subsequent nodes are inserted after
        // the last insertion
        destNode = resultNode;
        location = locationAfter;

        if (isMove) {
            try {
                sourceNode.deleteSelf();
            } catch (e) {
                throw new Error(errorRemoveOriginalFail);
            }
        }
    };

    return '';
}

export {
    executeAsync,
    locationFirstChild,
    locationLastChild,
    locationBefore,
    locationAfter,
    locationDescription,
};